import { MDText } from 'i18n-react';

import { MinMaxFilters } from 'Components/VehicleList/filters/Filters.helpers';
import { VEHICLE_CLASSES } from 'Components/VehicleList/VehicleListFilters.consts';

import texts from '../VehicleListV2.texts.json';

export const QUICK_FILTER_IDS = [
	'makeModel',
	'price',
	'mileage',
	'age',
	'distance',
	'vehicleType',
	'sellerType',
	'listingAttributes',
] as const;

export type QuickFilterId = (typeof QUICK_FILTER_IDS)[number];

export type RangeChipConfig = {
	clearMax?: boolean;
	fromId: string;
	id: QuickFilterId;
	increment: number;
	labelKey: string;
	max: number;
	min: number;
	toId: string;
	type: 'range';
	unit: 'distance' | 'miles' | 'price' | 'years';
};

export type SingleValueRangeChipConfig = {
	clearMax?: boolean;
	id: QuickFilterId;
	increment: number;
	labelKey: string;
	max: number;
	min: number;
	toId: string;
	type: 'singleValueRange';
	unit: 'distance' | 'miles' | 'price' | 'years';
};

// Array checkbox: selection stored as a string array on filterId
export type ArrayCheckboxChipConfig = {
	filterId: string;
	id: QuickFilterId;
	labelKey: string;
	type: 'array';
};

// Boolean checkbox: single selection mapped to a boolean on filterId
export type BooleanCheckboxChipConfig = {
	activeValue: string;
	filterId: string;
	id: QuickFilterId;
	invertSelection?: boolean;
	labelKey: string;
	type: 'boolean';
};

// Independent booleans: each option maps to its own boolean key in the filter store
export type IndependentBooleansChipConfig = {
	filterId: string;
	id: QuickFilterId;
	labelKey: string;
	type: 'independentBooleans';
};

export type CheckboxChipConfig = ArrayCheckboxChipConfig | BooleanCheckboxChipConfig | IndependentBooleansChipConfig;

export type MakeModelChipConfig = {
	id: 'makeModel';
	labelKey: string;
	type: 'makeModel';
};

export type FilterChipConfig = CheckboxChipConfig | MakeModelChipConfig | RangeChipConfig | SingleValueRangeChipConfig;

export const FILTER_CHIP_CONFIG: FilterChipConfig[] = [
	{
		id: 'makeModel',
		labelKey: 'filterBar.makeModel',
		type: 'makeModel',
	},
	{
		fromId: 'displayPriceFrom',
		id: 'price',
		increment: MinMaxFilters.price.INCREMENT,
		labelKey: 'filterBar.price',
		max: MinMaxFilters.price.MAX,
		min: MinMaxFilters.price.MIN,
		toId: 'displayPriceTo',
		type: 'range',
		unit: 'price',
	},
	{
		clearMax: false,
		fromId: 'mileageFrom',
		id: 'mileage',
		increment: MinMaxFilters.mileage.INCREMENT,
		labelKey: 'filterBar.mileage',
		max: MinMaxFilters.mileage.MAX,
		min: MinMaxFilters.mileage.MIN,
		toId: 'mileageTo',
		type: 'range',
		unit: 'miles',
	},
	{
		clearMax: false,
		fromId: 'ageFrom',
		id: 'age',
		increment: MinMaxFilters.age.INCREMENT,
		labelKey: 'filterBar.age',
		max: MinMaxFilters.age.MAX,
		min: MinMaxFilters.age.MIN,
		toId: 'ageTo',
		type: 'range',
		unit: 'years',
	},
	{
		clearMax: false,
		id: 'distance',
		increment: MinMaxFilters.distance.INCREMENT,
		labelKey: 'filterBar.distance',
		max: MinMaxFilters.distance.MAX,
		min: MinMaxFilters.distance.MIN,
		toId: 'maxDistance',
		type: 'singleValueRange',
		unit: 'distance',
	},
	{
		filterId: 'vehicleClass',
		id: 'vehicleType',
		labelKey: 'filterBar.vehicleType',
		type: 'array',
	},
	{
		filterId: 'sellerType',
		id: 'sellerType',
		labelKey: 'filterBar.stockSource',
		type: 'array',
	},
	{
		filterId: 'profileFeatures',
		id: 'listingAttributes',
		labelKey: 'filterBar.profileFeatures',
		type: 'independentBooleans',
	},
];

const LocalT = new MDText(texts);

export const ALL_OPTION_VALUE = '';

export const VEHICLE_TYPE_OPTIONS = [
	{ label: 'All', value: ALL_OPTION_VALUE },
	{ label: `${LocalT.translate('filterOptions.car')}`, value: VEHICLE_CLASSES.CAR },
	{ label: `${LocalT.translate('filterOptions.van')}`, value: VEHICLE_CLASSES.VAN },
];

export const SELLER_TYPE_OPTIONS = [
	{ label: 'All', value: ALL_OPTION_VALUE },
	{ label: `${LocalT.translate('filterOptions.exLease')}`, value: 'exLease' },
	{ label: `${LocalT.translate('filterOptions.private')}`, value: 'private' },
];

export const LISTING_ATTRIBUTE_OPTIONS = [
	{ label: 'All', value: ALL_OPTION_VALUE },
	{ label: `${LocalT.translate('filterOptions.paidListing')}`, value: 'isPaidListing' },
	// TODO: hasEarlyDocuments will be enabled as part of a later experiment
	// { label: `${LocalT.translate('filterOptions.docsReady')}`, value: 'hasEarlyDocuments' },
];

// Options without the 'All' sentinel — used where clearing is done via the chip itself.
export const LISTING_ATTRIBUTE_OPTIONS_NO_ALL = LISTING_ATTRIBUTE_OPTIONS.filter((o) => o.value !== ALL_OPTION_VALUE);

export const CHECKBOX_OPTIONS_MAP: Record<string, { label: string; value: string }[]> = {
	profileFeatures: LISTING_ATTRIBUTE_OPTIONS_NO_ALL,
	sellerType: SELLER_TYPE_OPTIONS,
	vehicleClass: VEHICLE_TYPE_OPTIONS,
};
